import { useMemo, useState, useEffect } from 'react';
import {
  JFormik,
  ScrollToRef,
  useScrollToTop,
  sortString,
} from '@centerpiece/components';
import { toast } from 'react-toastify';
import {
  useCreateOrganizationMutation,
  useCreateSubscriptionContactMutation,
  useGetOrganizationQuery,
  useGetOrganizationsQuery,
  useGetSubscriptionContactQuery,
  useGetSubscriptionContactsQuery,
  useGetTeamsQuery,
  useUpdateOrganizationMutation,
  useUpdateSubscriptionContactMutation,
} from 'store/api';
import { Organization, SubscriptionContact, Team } from 'store/api/models';
import { useContactForm } from 'jformik/useContactForm';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Container,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  Switch,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { ArrowBack, Edit, Warning } from '@mui/icons-material';
import { useOrganizationForm } from 'jformik/useOrganizationForm';

export interface ContactComponentProps {
  open: boolean;
  close: Function;
  disabled?: boolean;
  organization?: boolean;
  id?: number;
  category?: string;
  title?: string;
  callback?: Function;
  initialValues?: any;
  toggle?: boolean;
  enableOrganization?: boolean;
  fromMessage?: boolean;
}

export const ContactComponent = ({
  open,
  close,
  disabled = false,
  organization: isOrgForm = false,
  id,
  title,
  callback,
  initialValues,
  toggle = false,
  enableOrganization = false,
  fromMessage = false,
}: ContactComponentProps) => {
  const { data: subscription_contacts = [] } = useGetSubscriptionContactsQuery({
    params: { nested: 'all' },
  });
  const { data: organizations = [] } = useGetOrganizationsQuery({
    params: { nested: 'all' },
  });
  const [isDisabled, setIsDisabled] = useState<boolean>(disabled);
  const form = useContactForm({
    edit: !!id,
    roleType: 'External',
    organizations,
    enableOrganization,
  });

  const orgform = useOrganizationForm({
    edit: !!id,
    roleType: 'External',
  });
  const [organization, setOrganizationForm] = useState<boolean>(
    isOrgForm || initialValues?.type === 'organization'
  );
  useEffect(() => {
    setOrganizationForm(isOrgForm || initialValues?.type === 'organization');
  }, [isOrgForm, initialValues]);
  const handleToggle = () => {
    setOrganizationForm(prev => !prev);
  };
  const { data: scontact = {} as SubscriptionContact, isLoading } =
    useGetSubscriptionContactQuery(
      { id: id || 0, params: { nested: 'all' } },
      { skip: !id || organization }
    );
  const { data: orgcontact = {} as Organization, isLoading: isOrgLoading } =
    useGetOrganizationQuery(
      { id: id || 0, params: { nested: 'all' } },
      { skip: !id || !organization }
    );
  const { data: teams = [] as Team[], isLoading: teamsLoading } =
    useGetTeamsQuery({});
  const [createSubscriptionContact] = useCreateSubscriptionContactMutation();
  const [createOrganization] = useCreateOrganizationMutation();
  const [updateSubscriptionContact] = useUpdateSubscriptionContactMutation();
  const [updateOrganization] = useUpdateOrganizationMutation();
  return (
    <Box sx={{ px: 1 }}>
      {!id ? (
        <AppBar
          position="relative"
          sx={{ mb: 2, backgroundColor: 'transparent' }}
          elevation={0}
        >
          {/* <ScrollToRef /> */}
          <Toolbar disableGutters>
            <Container maxWidth="xl" sx={{ backgroundColor: 'white', py: 2 }}>
              <Grid
                container
                alignItems={'center'}
                justifyItems={'space-between'}
                justifyContent={'space-between'}
              >
                <Grid item>
                  <Tooltip title={'Back to previous'}>
                    <Button
                      color="secondary"
                      startIcon={
                        <div style={{ marginTop: '4px' }}>
                          <ArrowBack />
                        </div>
                      }
                      onClick={() => close()}
                      sx={{ pl: 0 }}
                    >
                      <Typography variant="h5" color={'primary'}>
                        {title || organization
                          ? 'Create Organization'
                          : 'Create Contact'}
                      </Typography>
                    </Button>
                  </Tooltip>
                  {fromMessage && (
                    <Alert
                      icon={<Warning fontSize="inherit" />}
                      severity="warning"
                    >
                      Reminder: Newly created contacts aren’t auto-added. After
                      creation, search for the contact and add them manually
                      before sending the message.
                    </Alert>
                  )}
                </Grid>
                {toggle && (
                  <Grid item>
                    <Tooltip title={'Toggle to create an organization'}>
                      <FormControl component="fieldset" variant="standard">
                        <FormGroup
                          sx={{ float: 'right' }}
                          onClick={() => {
                            handleToggle(); /// below isn't always working???
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                color="secondary"
                                checked={organization}
                                // onChange={() => { ///not always working???
                                //   handleToggle();
                                // }}
                              />
                            }
                            label={
                              <Typography variant="button">
                                Organization Only
                              </Typography>
                            }
                          />
                        </FormGroup>
                      </FormControl>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            </Container>
          </Toolbar>
        </AppBar>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            color="secondary"
            onClick={() => close()}
          >
            <Typography
              sx={{
                lineHeight: 1,
                marginTop: 0.25,
                fontSize: 15,
              }}
            >
              Back
            </Typography>
          </Button>
          {isDisabled && (
            <Button
              startIcon={<Edit />}
              color="secondary"
              onClick={() => setIsDisabled(false)}
            >
              <Typography
                sx={{
                  lineHeight: 1,
                  marginTop: 0.25,
                  fontSize: 15,
                }}
              >
                Edit
              </Typography>
            </Button>
          )}
        </Box>
      )}
      <Box sx={{ marginTop: 0, px: title ? 3 : 1 }}>
        <JFormik
          loading={isLoading || teamsLoading || isOrgLoading}
          disabled={isDisabled}
          initialValues={
            initialValues
              ? { ...(initialValues || {}), role: initialValues?.role || '' }
              : id
              ? organization
                ? {
                    ...orgcontact,
                    role: orgcontact.role_r || '',
                    type: 'organization',
                  }
                : {
                    ...scontact.contact_r,
                    role: scontact.role_r || '',
                    organization: scontact.organization_r || '',
                    name: `${scontact.contact_r?.firstname} ${scontact.contact_r?.lastname}`,
                    form: {
                      ...scontact.contact_r?.form,
                      team: teams.find(
                        (t: Team) => t.id == scontact.contact_r?.form?.team?.id
                      ),
                    },
                    id: scontact.id,
                  }
              : { role: '' }
          }
          enableReinitialize={true}
          handleSubmit={async (values, formik) => {
            if (id) {
              try {
                if (organization) {
                  await updateOrganization({
                    ...values,
                    role: values?.role?.id,
                  }).unwrap();
                } else {
                  await updateSubscriptionContact({
                    ...values,
                    organization: values?.organization?.id,
                    role: values?.role?.id,
                  }).unwrap();
                }
                close();
                toast.info(
                  `${
                    organization ? 'Organization' : 'Contact'
                  } updated successfuly`
                );
              } catch (error) {
                toast.error('Unable to update contact');
              }
            } else {
              try {
                let res: any = {};
                if (organization) {
                  res = await createOrganization({
                    ...values,
                    role: values?.role?.id,
                  }).unwrap();
                  res = {
                    ...res,
                    type: 'organization',
                    organization_r: { ...res, type: 'organization' },
                    contact_r: { ...res, type: 'organization' },
                  };
                } else {
                  res = await createSubscriptionContact({
                    ...values,
                    organization: values?.organization?.id,
                    role: values?.role?.id,
                  }).unwrap();
                }
                close();
                callback && callback(res);
                toast.info('Contact created successfuly');
              } catch (error) {
                toast.error('Unable to create contact');
              }
            }
          }}
          validationSchema={
            organization ? orgform.validationSchema : form.validationSchema
          }
          form={organization ? orgform.form : form.form}
        />
      </Box>
    </Box>
  );
};
